<template>
  <div
    class="installation"
  >
    <main-menu />
    <transition name="default-opacity-transition" mode="out-in">
      <s-loader
        class="mt-5"
        size="md"
        v-if="!typesWithAssets"
      />
      <router-view
        class="installation__content"
        v-else
        :key="$route.params.installationId"
        :has-single-asset="hasSingleAsset"
        :types-with-assets="typesWithAssets"
      />
    </transition>
  </div>
</template>

<script>

import orderBy from 'lodash/orderBy';
import InstallationService from '@/domain/services/installation';
import AssetService from '@/domain/services/asset';
import MenuService from '@/domain/services/installation/modules/menu';
import { mapGetters } from 'vuex';
import TypesWithAssetsMixin from '@/domain/mixins/installation/typesWithAssetsMixin';
import installationMenu from '@/domain/data/installation/installation-menu.json';
import MainMenu from '@/domain/components/layout/Menu';
import treeviewMixin from '@/components/treeview/assetTreeviewProviderMixin';
import globalEventBus from '@/services/globalEventBus';
import menuEvents from '@/domain/events/menu';
import store from '@/store';

export default {
  components: {
    MainMenu,
  },
  mixins: [
    TypesWithAssetsMixin,
    treeviewMixin,
  ],
  data: () => ({
    installationMenu,
    latestLoadedTypesWithAssets: null,
    latestLoadedInstallationId: null,
  }),
  computed: {
    ...mapGetters([
      'installation',
    ]),
    currentInstallationId() {
      return this.$route.params.installationId;
    },
    currentAssetId() {
      return this.$route.params.assetId;
    },
    typesWithAssets() {
      this.loadAssetsData();
      return orderBy(this.latestLoadedTypesWithAssets, 'name');
    },
  },
  async created() {
    await this.loadAssetsData();
    await this.loadMenuData();
  },
  methods: {
    async loadMenuData() {
      if (!this.currentAssetId) {
        return;
      }
      const menuItems = await MenuService.getMenuItems(this.currentAssetId);
      globalEventBus.emit(menuEvents.refreshMenuItems, menuItems);
      await store.dispatch('setAssetSubMenuItems', menuItems);
    },
    async loadAssetsData() {
      if (this.latestLoadedInstallationId === this.currentInstallationId) {
        return;
      }

      this.latestLoadedTypesWithAssets = null;
      this.latestLoadedInstallationId = this.currentInstallationId;

      const [assetTypes, allAssets] = await Promise.all([
        AssetService.getAssetTypes(),
        AssetService.getAssets({ installationId: this.currentInstallationId }),
      ]);

      const groupedAssets = {};
      allAssets.forEach((asset) => {
        if (!groupedAssets[asset.assetTypeId]) {
          groupedAssets[asset.assetTypeId] = [];
        }

        groupedAssets[asset.assetTypeId].push(asset);
      });

      const assetTypesWithData = assetTypes.map(assetType => ({
        ...assetType,
        assets: groupedAssets[assetType.id],
      }));
      this.latestLoadedTypesWithAssets = assetTypesWithData
        .filter(({ assets }) => assets !== undefined);
    },
    hasSingleAsset(module) {
      if (!this.latestLoadedTypesWithAssets) return false;
      const filteredTypesAndAssets = this.getFilteredTypesAndAssetsByAppFeature(module);
      const typesWithAssetsLength = filteredTypesAndAssets.length;
      const assetsLength = filteredTypesAndAssets[0]
        ? filteredTypesAndAssets[0].assets.length
        : 0;
      return typesWithAssetsLength === 1 && assetsLength === 1;
    },
  },
  beforeRouteEnter(to, from, next) {
    InstallationService.loadAccessInstallationData(to.params.installationId)
      .then(() => next());
  },
  beforeRouteUpdate(to, from, next) {
    if (from.params.installationId !== to.params.installationId) {
      InstallationService.loadAccessInstallationData(to.params.installationId)
        .then(() => next());

      return;
    }

    next();
  },
  beforeRouteLeave(to, from, next) {
    next();
    InstallationService.clearAccessInstallationData();
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/mobile-first"

  .installation
    display: flex
    flex-direction: column
    flex: 1
    transition: padding-left .5s .1s

    @media not print
    +media-min-sm()
    +media-min-md()
    +media-min-lg()
</style>
